import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import MockupHeader from './mockup/MockupHeader';
import MockupSidebar from './mockup/MockupSidebar';
import MockupCursor from './mockup/MockupCursor';
import DashboardContent from './mockup/content/DashboardContent';
import WorkflowsContent from './mockup/content/WorkflowsContent';
import PlannerContent from './mockup/content/PlannerContent';
import EmailContent from './mockup/content/EmailContent';
import TemplatesContent from './mockup/content/TemplatesContent';
import InboxContent from './mockup/content/InboxContent';
import FunnelBuilderContent from './mockup/content/FunnelBuilderContent';
import PipelineContent from './mockup/content/PipelineContent';
import ReputationContent from './mockup/content/ReputationContent';
import CalendarContent from './mockup/content/CalendarContent';
import AIAgentsContent from './mockup/content/AIAgentsContent';
import AIContentGeneratorContent from './mockup/content/AIContentGeneratorContent';
import CourseBuilderContent from './mockup/content/CourseBuilderContent';
import AdsManagerContent from './mockup/content/AdsManagerContent';
import PaymentsContent from './mockup/content/PaymentsContent';

interface AnimatedCRMMockupProps {
  activeTab: number;
  customBrand?: {
    name: string;
    icon?: React.ReactNode;
  };
}

const AnimatedCRMMockup: React.FC<AnimatedCRMMockupProps> = ({ activeTab, customBrand }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  // Removed animationKey state - it caused full re-renders and layout thrashing
  // The individual content components handle their own animations

  // Render content based on active tab
  const renderContent = () => {
    const delay = 400;
    switch (activeTab) {
      case 0:
        return <DashboardContent animationDelay={delay} />;
      case 1:
        return <WorkflowsContent animationDelay={delay} />;
      case 2:
        return <PlannerContent animationDelay={delay} />;
      case 3:
        return <EmailContent animationDelay={delay} />;
      case 4:
        return <TemplatesContent animationDelay={delay} />;
      case 5:
        return <InboxContent animationDelay={delay} />;
      case 6:
        return <FunnelBuilderContent animationDelay={delay} />;
      case 7:
        return <PipelineContent animationDelay={delay} />;
      case 8:
        return <ReputationContent animationDelay={delay} />;
      case 9:
        return <CalendarContent animationDelay={delay} />;
      case 10:
        return <AIAgentsContent animationDelay={delay} />;
      case 11:
        return <AIContentGeneratorContent animationDelay={delay} />;
      case 12:
        return <CourseBuilderContent animationDelay={delay} />;
      case 13:
        return <AdsManagerContent animationDelay={delay} />;
      case 14:
        return <PaymentsContent animationDelay={delay} />;
      default:
        return <DashboardContent animationDelay={delay} />;
    }
  };

  return (
    <div 
      className="relative overflow-hidden w-full transition-colors duration-300"
      style={{ 
        borderRadius: '12px',
        border: `1px solid ${isDark ? '#333333' : '#E0E0E0'}`,
        boxShadow: isDark 
          ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(59, 130, 246, 0.15)'
          : '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 40px rgba(59, 130, 246, 0.1)'
      }}
    >
      {/* Main mockup container */}
      <div
        className="flex flex-col w-full transition-colors duration-300"
        style={{ 
          minHeight: '200px',
          backgroundColor: isDark ? '#222222' : '#FFFFFF'
        }}
      >
        {/* Header */}
        <MockupHeader animationDelay={0} customBrand={customBrand} />

        {/* Body: Sidebar + Content */}
        <div className="flex flex-1 min-h-[160px] sm:min-h-[180px] md:min-h-[260px] lg:min-h-[460px] overflow-hidden">
          {/* Sidebar */}
          <MockupSidebar activeTab={activeTab} animationDelay={200} />

          {/* Main Content - Tab specific */}
          {renderContent()}
        </div>
      </div>

      {/* Animated cursor with click effects */}
      <MockupCursor key={`cursor-${theme}`} activeTab={activeTab} />

      {/* Bottom fade overlay */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
        style={{
          background: isDark 
            ? 'linear-gradient(to top, rgba(34,34,34,1) 0%, rgba(34,34,34,0) 100%)'
            : 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)'
        }}
      />
      
      {/* Right fade overlay */}
      <div 
        className="absolute top-0 bottom-0 right-0 w-12 pointer-events-none"
        style={{
          background: isDark 
            ? 'linear-gradient(to left, rgba(34,34,34,1) 0%, rgba(34,34,34,0) 100%)'
            : 'linear-gradient(to left, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)'
        }}
      />
    </div>
  );
};

export default AnimatedCRMMockup;
